import { useEffect } from "react";
import { Link } from "react-router-dom";
import { HARD_STATS, STATS_CARDS, CHALLENGE_BANNER, CHALLENGE_DAY1, CALENDAR_IMG, ACTION_GUIDE_IMG, FCA_LOGO, KIDS, FOSTER_STORIES_SUNSET } from "../data/kids";
import SEO from "../components/SEO";

const CATEGORIES = [
  { i: "📖", t: "Learn", d: "Watch a 60-second story from a former foster youth. Share what you learned." },
  { i: "💬", t: "Share", d: "Post a quote or stat about foster care using #Foster365" },
  { i: "🤝", t: "Give", d: "Donate $5 or a comfort item to a local agency" },
  { i: "❤️", t: "Act", d: "Sign up to be a mentor, respite provider, or learn about fostering" },
  { i: "📸", t: "Spotlight", d: "Feature a youth success story, caseworker hero, or foster family" },
];

export default function EverydayAwareness() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="everyday-awareness-page">
      <SEO
        path="/everyday-awareness"
        title="Foster Care Awareness Every Day | The #Foster365 Challenge"
        description="Awareness shouldn't last one month — it should last 365 days. Join the #Foster365 Challenge: Learn, Share, Give, Act, Spotlight. 31 days of action, infinite impact. Includes daily wall calendar & action guide."
      />
      <section className="border-b border-fc-border">
        <div className="container-xl pt-20 pb-12">
          <div className="overline text-fc-accent mb-5">Everyday Awareness & Action</div>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-5xl">
            Advocacy is a <span className="italic text-fc-accent">365-Day Commitment.</span>
          </h1>
          <p className="mt-6 text-fc-muted text-lg max-w-3xl leading-relaxed">
            National Foster Care Month (May) is a vital spark, but the children in our communities need visibility every single day. We are here to bridge the gap between "awareness" and "action."
          </p>
          <blockquote className="mt-10 pl-6 border-l-4 border-fc-accent font-display text-2xl md:text-3xl italic leading-tight max-w-4xl">
            "This platform is a vessel for stories that often go unheard. By centering the narratives of the youth themselves, we move past statistics and into the heart of the mission."
          </blockquote>
        </div>
      </section>

      {/* Hard stats */}
      <section className="bg-fc-ink text-fc-bg relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.18]">
          <img src={FOSTER_STORIES_SUNSET} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-fc-ink via-fc-ink/85 to-fc-ink/40" />
        </div>
        <div className="container-xl py-16 relative">
          <div className="overline mb-6 opacity-70">The Why · Shocking Stats That Demand Action</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0 border-t border-l border-fc-bg/15">
            {HARD_STATS.map((s, i) => (
              <div key={i} className="p-6 md:p-7 border-r border-b border-fc-bg/15 backdrop-blur-[2px]">
                <div className="font-display text-3xl md:text-4xl text-fc-accent leading-none mb-3">{s.num}</div>
                <div className="text-xs md:text-sm leading-snug opacity-80">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stat cards gallery */}
      <section className="bg-fc-surface border-y border-fc-border">
        <div className="container-xl py-16">
          <div className="overline text-fc-muted mb-4">Awareness Cards · Share + Act</div>
          <h2 className="font-display text-3xl md:text-5xl mb-10 leading-tight">The realities we can't un-see.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {STATS_CARDS.map((c, i) => (
              <div key={i} className="border border-fc-border overflow-hidden bg-fc-bg">
                <img src={c.url} alt={c.alt} className="w-full h-auto block" loading="lazy" />
                <div className="px-4 py-3 overline text-fc-muted">{c.alt}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The 4 kids link bar */}
      <section className="container-xl py-16">
        <div className="grid grid-cols-4 gap-3">
          {KIDS.map((k) => (
            <Link key={k.slug} to={`/meet-the-kids/${k.slug}`} className="group block border border-fc-border bg-fc-bg card-hover" data-testid={`awareness-kid-${k.slug}`}>
              <div className="aspect-[3/4] overflow-hidden">
                <img src={k.hero} alt={k.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
              </div>
              <div className="px-3 py-3 overline text-center">Meet {k.name}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* #Foster365 Challenge */}
      <section className="bg-fc-ink text-fc-bg">
        <div className="container-xl py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <div className="overline text-fc-accent mb-4">May · 31 Days of Action</div>
            <h2 className="font-display text-4xl md:text-6xl leading-tight">The <span className="italic text-fc-accent">#Foster365</span> Challenge.</h2>
            <p className="mt-6 opacity-75 leading-relaxed max-w-xl">
              Every day counts. Every child matters. Take one small action each day in May to help children and families in foster care — and keep the habit going all year.
            </p>
            <div className="mt-8 grid grid-cols-5 gap-2">
              {CATEGORIES.map((c) => (
                <div key={c.t} className="text-center border border-fc-bg/20 py-4">
                  <div className="text-xl">{c.i}</div>
                  <div className="overline mt-1 opacity-80">{c.t}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <img src={CHALLENGE_BANNER} alt="Foster365 Challenge" className="w-full border border-fc-bg/20" />
          </div>
        </div>
      </section>

      {/* Daily categories deep */}
      <section className="container-xl py-20">
        <h2 className="font-display text-3xl md:text-5xl mb-10">Daily Challenge Categories.</h2>
        <div className="grid md:grid-cols-5 gap-0 border border-fc-border">
          {CATEGORIES.map((c, i) => (
            <div key={c.t} className={`p-7 ${i<4 ? "md:border-r" : ""} border-fc-border`}>
              <div className="text-3xl mb-3">{c.i}</div>
              <div className="font-display text-xl mb-2">{c.t}</div>
              <p className="text-sm text-fc-muted leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Calendar + Action Guide downloads */}
      <section className="bg-fc-surface border-y border-fc-border">
        <div className="container-xl py-20 grid md:grid-cols-2 gap-8">
          <div className="border border-fc-border bg-fc-bg p-6">
            <div className="overline text-fc-muted mb-3">Mark Your Calendar</div>
            <h3 className="font-display text-2xl mb-4">365 Daily Wall Calendar</h3>
            <img src={CALENDAR_IMG} alt="Daily Wall Calendar" className="w-full border border-fc-border mb-5" loading="lazy" />
            <a href={CALENDAR_IMG} target="_blank" rel="noopener noreferrer" className="btn-outline" data-testid="download-calendar">Download Calendar</a>
          </div>
          <div className="border border-fc-border bg-fc-bg p-6">
            <div className="overline text-fc-muted mb-3">Let Your Actions Be Your Guide</div>
            <h3 className="font-display text-2xl mb-4">365 Daily Action Guide</h3>
            <img src={ACTION_GUIDE_IMG} alt="Daily Action Guide" className="w-full border border-fc-border mb-5" loading="lazy" />
            <a href={ACTION_GUIDE_IMG} target="_blank" rel="noopener noreferrer" className="btn-outline" data-testid="download-guide">Download Action Guide</a>
          </div>
        </div>
      </section>

      {/* FCA Logo callout */}
      <section className="container-xl py-20 text-center max-w-3xl">
        <img src={FCA_LOGO} alt="Foster Care Awareness 365 Logo" className="w-32 mx-auto mb-6" />
        <blockquote className="font-display text-3xl md:text-4xl italic leading-tight">
          "These aren't just numbers. They're urgent calls to act — <span className="text-fc-accent">365 days a year.</span>"
        </blockquote>
        <div className="mt-10 flex gap-3 justify-center flex-wrap">
          <Link to="/get-involved" className="btn-primary" data-testid="awareness-give">Ways to Give</Link>
          <Link to="/meet-the-kids" className="btn-outline" data-testid="awareness-kids">Meet the Kids</Link>
        </div>
      </section>
    </div>
  );
}
